import { useEffect, useRef, useCallback } from "react";
import { useBluelagoon } from "@bluelagoon";
import { useEditor } from "../EditorContext";
import { useChatContext } from "../ChatContext";

const BATCH_SIZE = 50;
const BATCH_DELAY_MS = 800;

export function useCodePrefetch(components) {
    const { fileStates, loadFileCode, prefetchCode, prefetchedCode } = useEditor();
    const { selectedEnvironment } = useChatContext();
    const { call: fetchCode } = useBluelagoon({ method: "getComponentCode" });
    const { call: fetchCodeBulk } = useBluelagoon({
        method: "getComponentCodeBulk",
        setIsLoading: false,
    });
    const loadedCodeRef = useRef(new Set());
    const prefetchedCodeRef = useRef(prefetchedCode);
    prefetchedCodeRef.current = prefetchedCode;
    const prevComponentsRef = useRef(components);

    // Reset cache when environment changes
    useEffect(() => {
        loadedCodeRef.current = new Set();
    }, [selectedEnvironment]);

    // Clear cache when components list is refreshed (AI update, save, etc.)
    // so the bulk prefetch re-fetches fresh code
    useEffect(() => {
        if (prevComponentsRef.current !== components && prevComponentsRef.current.length > 0) {
            loadedCodeRef.current = new Set();
        }
        prevComponentsRef.current = components;
    }, [components]);

    // Fetch a single component's code (for on-demand file open)
    const ensureCode = useCallback(
        (component) => {
            const cacheKey = `${component.id}:${selectedEnvironment || "production"}`;

            // Already fetched — but file may have just been opened and needs
            // code loaded into fileStates from the prefetch cache
            if (loadedCodeRef.current.has(cacheKey)) {
                const cached = prefetchedCodeRef.current[component.id];
                if (cached != null) {
                    loadFileCode(component.id, cached);
                }
                return;
            }
            loadedCodeRef.current.add(cacheKey);

            fetchCode({
                id: component.id,
                environmentName: selectedEnvironment,
            }).then((res) => {
                if (res?.data?.code != null) {
                    loadFileCode(component.id, res.data.code);
                    prefetchCode(component.id, res.data.code);
                }
            });
        },
        [fetchCode, selectedEnvironment, loadFileCode, prefetchCode]
    );

    // Background bulk prefetch for search
    useEffect(() => {
        if (components.length === 0) return;

        let cancelled = false;

        const prefetchAll = async () => {
            // Filter to components not yet loaded
            const env = selectedEnvironment || "production";
            const needed = components.filter(
                (c) => !loadedCodeRef.current.has(`${c.id}:${env}`)
            );
            if (needed.length === 0) return;

            for (let i = 0; i < needed.length; i += BATCH_SIZE) {
                if (cancelled) return;
                const batch = needed.slice(i, i + BATCH_SIZE);
                const ids = batch.map((c) => c.id);

                // Mark as loading so we don't double-fetch
                ids.forEach((id) => loadedCodeRef.current.add(`${id}:${env}`));

                try {
                    const res = await fetchCodeBulk({
                        ids,
                        environmentName: selectedEnvironment,
                    });
                    if (cancelled) return;
                    const items = res?.data?.components || [];
                    items.forEach((item) => {
                        if (item.code != null) {
                            prefetchCode(item.id, item.code);
                            loadFileCode(item.id, item.code);
                        }
                    });
                } catch {
                    // Un-mark failed ones so they can retry
                    ids.forEach((id) => loadedCodeRef.current.delete(`${id}:${env}`));
                }

                if (cancelled || i + BATCH_SIZE >= needed.length) return;
                await new Promise((r) => setTimeout(r, BATCH_DELAY_MS));
            }
        };

        prefetchAll();
        return () => { cancelled = true; };
    }, [components, selectedEnvironment, fetchCodeBulk, prefetchCode, loadFileCode]);

    // Invalidate cache for a component so the next ensureCode re-fetches
    const invalidateCode = useCallback(
        (componentId) => {
            const env = selectedEnvironment || "production";
            loadedCodeRef.current.delete(`${componentId}:${env}`);
        },
        [selectedEnvironment]
    );

    return { ensureCode, invalidateCode, loadedCodeRef };
}
